package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.swetdot.swetgram.domain.GroupPostingPolicy
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetLoading
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.profile.ProfileSectionHeader
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.group_settings_admins_only
import swetgram.shared.generated.resources.group_settings_chat
import swetgram.shared.generated.resources.group_settings_description
import swetgram.shared.generated.resources.group_settings_discover
import swetgram.shared.generated.resources.group_settings_everyone
import swetgram.shared.generated.resources.group_settings_identity
import swetgram.shared.generated.resources.group_settings_name
import swetgram.shared.generated.resources.group_settings_posting
import swetgram.shared.generated.resources.group_settings_save
import swetgram.shared.generated.resources.group_settings_title
import swetgram.shared.generated.resources.group_settings_unavailable
import swetgram.shared.generated.resources.group_settings_username

@Composable
fun GroupSettingsScreen(
    state: GroupSettingsUiState,
    onFormChange: (GroupSettingsForm) -> Unit,
    onSave: () -> Unit,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onDismissMessage: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val form = state.form
    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(title = stringResource(Res.string.group_settings_title), onBack = onBack)
        state.error?.let {
            SwetErrorBanner(message = it, onRetry = onRetry, onDismiss = onDismissMessage)
        }
        state.notice?.let {
            SwetErrorBanner(message = it, onDismiss = onDismissMessage)
        }
        when {
            state.group == null && !state.loadedOnce -> SwetLoading()
            state.group == null -> SwetEmptyState(
                title = stringResource(Res.string.group_settings_unavailable),
                body = stringResource(Res.string.group_settings_unavailable),
                actionText = "Try again",
                onAction = onRetry,
            )
            else -> Column(
                Modifier
                    .fillMaxSize()
                    .imePadding()
                    .navigationBarsPadding()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = Space.screenMargin, vertical = Space.two),
            ) {
                if (state.canEditIdentity) {
                    ProfileSectionHeader(stringResource(Res.string.group_settings_identity))
                    SwetTextField(
                        value = form.name,
                        onValueChange = { onFormChange(form.copy(name = it)) },
                        label = stringResource(Res.string.group_settings_name),
                        placeholder = stringResource(Res.string.group_settings_name),
                        enabled = !state.saving,
                    )
                    Spacer(Modifier.height(Space.two))
                    SwetTextField(
                        value = form.description,
                        onValueChange = { onFormChange(form.copy(description = it)) },
                        label = stringResource(Res.string.group_settings_description),
                        placeholder = stringResource(Res.string.group_settings_description),
                        enabled = !state.saving,
                        singleLine = false,
                    )
                    if (state.group.visibility == GroupVisibility.PUBLIC) {
                        Spacer(Modifier.height(Space.two))
                        SwetTextField(
                            value = form.username,
                            onValueChange = { onFormChange(form.copy(username = it.lowercase())) },
                            label = stringResource(Res.string.group_settings_username),
                            placeholder = "community",
                            enabled = !state.saving,
                        )
                    }
                    Spacer(Modifier.height(Space.three))
                }

                if (state.canEditPolicies) {
                    ProfileSectionHeader(stringResource(Res.string.group_settings_posting))
                    PolicyRow(
                        title = stringResource(Res.string.group_settings_chat),
                        policy = form.chatPosting,
                        enabled = !state.saving,
                        onChange = { onFormChange(form.copy(chatPosting = it)) },
                    )
                    PolicyRow(
                        title = stringResource(Res.string.group_settings_discover),
                        policy = form.discoverPosting,
                        enabled = !state.saving,
                        onChange = { onFormChange(form.copy(discoverPosting = it)) },
                    )
                    Text(
                        text = if (
                            form.chatPosting == GroupPostingPolicy.ALL_MEMBERS &&
                            form.discoverPosting == GroupPostingPolicy.ALL_MEMBERS
                        ) stringResource(Res.string.group_settings_everyone)
                        else stringResource(Res.string.group_settings_admins_only),
                        style = MaterialTheme.typography.bodySmall,
                        color = SwetgramThemeTokens.colors.textSecondary,
                    )
                    Spacer(Modifier.height(Space.three))
                }

                SwetPrimaryButton(
                    text = stringResource(Res.string.group_settings_save),
                    onClick = onSave,
                    loading = state.saving,
                )
                Spacer(Modifier.height(Space.six))
            }
        }
    }
}

@Composable
private fun PolicyRow(
    title: String,
    policy: GroupPostingPolicy,
    enabled: Boolean,
    onChange: (GroupPostingPolicy) -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().padding(vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(title, modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyLarge)
        Switch(
            checked = policy == GroupPostingPolicy.ALL_MEMBERS,
            onCheckedChange = {
                onChange(if (it) GroupPostingPolicy.ALL_MEMBERS else GroupPostingPolicy.ADMINS_ONLY)
            },
            enabled = enabled,
        )
    }
}
